import React, { useState } from "react";
import { X, Shield } from "lucide-react";
import { COMPANY } from "@/data/feniks";

export default function PrivacyPolicyModal({ open, onClose }) {
  if (!open) return null;
  return (
    <div
      data-testid="privacy-modal"
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative bg-white max-w-3xl w-full max-h-[85vh] flex flex-col border border-zinc-200 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-zinc-200 px-6 py-4 bg-zinc-50">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-[var(--feniks-red)] text-white">
              <Shield size={18} />
            </div>
            <div>
              <div className="font-display text-lg tracking-tight">Polityka prywatności</div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">
                {COMPANY.name} · RODO
              </div>
            </div>
          </div>
          <button
            data-testid="privacy-close-btn"
            onClick={onClose}
            className="p-1.5 hover:bg-zinc-200 transition-colors"
            aria-label="zamknij"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto feniks-scroll p-6 md:p-8 space-y-5 text-sm leading-relaxed text-zinc-700">
          <section>
            <h3 className="font-display text-base tracking-tight text-zinc-950 mb-2">
              1. Administrator danych
            </h3>
            <p>
              Administratorem Twoich danych osobowych jest <strong>{COMPANY.name}</strong>,
              ul. Tyska 2, 40-655 Katowice, NIP: {COMPANY.nip}.
              Kontakt:{" "}
              <a href={`mailto:${COMPANY.email}`} className="text-[var(--feniks-red)] underline">
                {COMPANY.email}
              </a>{" "}
              · tel.{" "}
              <a href={`tel:${COMPANY.phoneRaw}`} className="text-[var(--feniks-red)] underline">
                {COMPANY.phone}
              </a>
              .
            </p>
          </section>

          <section>
            <h3 className="font-display text-base tracking-tight text-zinc-950 mb-2">
              2. Jakie dane zbieramy
            </h3>
            <ul className="list-disc list-inside space-y-1.5">
              <li>
                <strong>Formularz kontaktowy:</strong> imię i nazwisko, numer telefonu,
                adres e-mail, typ urządzenia, miasto, opis usterki.
              </li>
              <li>
                <strong>Czat z asystentem AI:</strong> treść Twoich wiadomości oraz
                identyfikator sesji (przechowywany lokalnie w przeglądarce).
              </li>
              <li>
                <strong>Dane techniczne:</strong> adres IP, typ przeglądarki — przetwarzane
                wyłącznie w celu zapewnienia bezpieczeństwa i prawidłowego działania strony
                (Cloudflare).
              </li>
            </ul>
          </section>

          <section>
            <h3 className="font-display text-base tracking-tight text-zinc-950 mb-2">
              3. Cel i podstawa prawna przetwarzania
            </h3>
            <ul className="list-disc list-inside space-y-1.5">
              <li>
                <strong>Realizacja zgłoszenia serwisowego</strong> – art. 6 ust. 1 lit. b RODO
                (działania na żądanie osoby przed zawarciem umowy).
              </li>
              <li>
                <strong>Odpowiedź na zapytanie</strong> – art. 6 ust. 1 lit. f RODO (prawnie
                uzasadniony interes administratora — kontakt z potencjalnym klientem).
              </li>
              <li>
                <strong>Bezpieczeństwo strony</strong> – art. 6 ust. 1 lit. f RODO.
              </li>
            </ul>
          </section>

          <section>
            <h3 className="font-display text-base tracking-tight text-zinc-950 mb-2">
              4. Pliki cookies i podobne technologie
            </h3>
            <p className="mb-2">
              Strona wykorzystuje{" "}
              <strong>wyłącznie niezbędne pliki cookies i pamięć przeglądarki</strong> (Local
              Storage). Nie używamy plików marketingowych, analitycznych ani
              śledzących.
            </p>
            <div className="border border-zinc-200 overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-zinc-50">
                  <tr className="text-left">
                    <th className="px-3 py-2 font-bold">Nazwa</th>
                    <th className="px-3 py-2 font-bold">Cel</th>
                    <th className="px-3 py-2 font-bold">Czas</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-zinc-200">
                    <td className="px-3 py-2 font-mono">feniks_chat_sid</td>
                    <td className="px-3 py-2">Identyfikator sesji asystenta AI</td>
                    <td className="px-3 py-2">do wyczyszczenia</td>
                  </tr>
                  <tr className="border-t border-zinc-200">
                    <td className="px-3 py-2 font-mono">feniks_cookie_consent</td>
                    <td className="px-3 py-2">Zapamiętanie Twojej zgody</td>
                    <td className="px-3 py-2">12 miesięcy</td>
                  </tr>
                  <tr className="border-t border-zinc-200">
                    <td className="px-3 py-2 font-mono">__cf_bm</td>
                    <td className="px-3 py-2">Ochrona przed botami (Cloudflare)</td>
                    <td className="px-3 py-2">30 minut</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mt-2 text-xs text-zinc-500">
              Możesz w każdej chwili wyczyścić te dane w ustawieniach swojej przeglądarki.
            </p>
          </section>

          <section>
            <h3 className="font-display text-base tracking-tight text-zinc-950 mb-2">
              5. Odbiorcy danych
            </h3>
            <ul className="list-disc list-inside space-y-1.5">
              <li>
                <strong>SendGrid (Twilio Inc.)</strong> – dostarczenie wiadomości e-mail
                z formularza kontaktowego.
              </li>
              <li>
                <strong>OpenAI / Emergent</strong> – obsługa czatu z asystentem AI
                (treść wiadomości jest przesyłana do modelu językowego).
              </li>
              <li>
                <strong>Cloudflare</strong> – sieć dostarczania treści i ochrona przed atakami.
              </li>
            </ul>
          </section>

          <section>
            <h3 className="font-display text-base tracking-tight text-zinc-950 mb-2">
              6. Czas przechowywania danych
            </h3>
            <p>
              Dane ze zgłoszeń serwisowych przechowujemy przez okres niezbędny do realizacji
              usługi i rozliczenia (maksymalnie 5 lat zgodnie z przepisami podatkowymi).
              Treść rozmów z asystentem AI jest usuwana po 30 dniach.
            </p>
          </section>

          <section>
            <h3 className="font-display text-base tracking-tight text-zinc-950 mb-2">
              7. Twoje prawa
            </h3>
            <p className="mb-2">Masz prawo do:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>dostępu do swoich danych,</li>
              <li>ich sprostowania, usunięcia lub ograniczenia przetwarzania,</li>
              <li>przenoszenia danych,</li>
              <li>wniesienia sprzeciwu wobec przetwarzania,</li>
              <li>
                wniesienia skargi do Prezesa Urzędu Ochrony Danych Osobowych (uodo.gov.pl).
              </li>
            </ul>
            <p className="mt-2">
              Aby skorzystać z tych praw, napisz na{" "}
              <a href={`mailto:${COMPANY.email}`} className="text-[var(--feniks-red)] underline">
                {COMPANY.email}
              </a>
              .
            </p>
          </section>

          <p className="text-[11px] text-zinc-500 font-mono pt-4 border-t border-zinc-200">
            Ostatnia aktualizacja: czerwiec 2026
          </p>
        </div>

        <div className="border-t border-zinc-200 px-6 py-4 bg-zinc-50 flex justify-end">
          <button
            onClick={onClose}
            className="bg-zinc-950 text-white px-6 py-3 text-xs font-bold uppercase tracking-widest hover:bg-[var(--feniks-red)] transition-colors"
            data-testid="privacy-acknowledge-btn"
          >
            Rozumiem
          </button>
        </div>
      </div>
    </div>
  );
}
